/* protec-gallery.js: arrows, swipe, a "3 of 11" count and arrow keys for every product gallery.

   The product sections already build their galleries, and three of them carry behaviour this file
   must not break: Kelvin jumps to the photo of the chosen finish, LumaBed shows only the current
   finish's photos, CryoLink has a dimensions drawing and a video among its frames. So this file
   never owns what a photo IS. It only ever moves by pressing the neighbouring VISIBLE thumbnail,
   exactly as a shopper would, and each section's own handler does the rest.

   The one exception: a .pp-g gallery with no section handler of its own (PROBOOTS 2) is activated
   here too. The activation writes the same classes and attributes the section handlers write, so
   running it where a handler also exists changes nothing.

   Gallery patterns recognised, in the order they are tried:
     [data-gallery]   thumbs [data-g-go], frames [data-g-frame], stage .pp-g__stage / .lb-g__stage
     .gallery         (sauna) thumbs .thumb, stage .stage, active thumb aria-selected="true"
     .pdp-gallery     (generic) thumbs .pdp-thumb, stage .pdp-gallery__main
*/
(function () {
  "use strict";
  var SVG_PREV = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7"/></svg>';
  var SVG_NEXT = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg>';

  function arr(nodes) { return Array.prototype.slice.call(nodes); }
  function shown(el) { return !el.hidden && !el.closest("[hidden]"); }

  function isActive(t) {
    return t.classList.contains("is-active") || t.classList.contains("active") ||
      t.getAttribute("aria-selected") === "true" || t.getAttribute("aria-current") === "true";
  }

  function wire(g, cfg) {
    var stage = g.querySelector(cfg.stage);
    if (!stage || g.hasAttribute("data-pr-gal")) return;
    g.setAttribute("data-pr-gal", "");

    function thumbs() { return arr(g.querySelectorAll(cfg.thumb)).filter(shown); }

    /* activation for .pp-g galleries (idempotent with the section handlers that exist) */
    if (cfg.frames) {
      arr(g.querySelectorAll(cfg.thumb)).forEach(function (t) {
        t.addEventListener("click", function () {
          var idx = t.getAttribute("data-g-go");
          arr(g.querySelectorAll("[data-g-frame]")).forEach(function (f) {
            var on = f.getAttribute("data-g-frame") === idx;
            f.classList.toggle("is-active", on);
            f.setAttribute("aria-hidden", on ? "false" : "true");
          });
          arr(g.querySelectorAll(cfg.thumb)).forEach(function (o) {
            var on = o === t;
            o.classList.toggle("is-active", on);
            o.setAttribute("aria-current", on ? "true" : "false");
            o.setAttribute("tabindex", on ? "0" : "-1");
          });
        });
      });
    }

    stage.classList.add("pr-gal-host");
    var prev = document.createElement("button");
    prev.type = "button"; prev.className = "pr-gal__arrow pr-gal__arrow--prev";
    prev.setAttribute("aria-label", "Previous photo"); prev.innerHTML = SVG_PREV;
    var next = document.createElement("button");
    next.type = "button"; next.className = "pr-gal__arrow pr-gal__arrow--next";
    next.setAttribute("aria-label", "Next photo"); next.innerHTML = SVG_NEXT;
    var count = document.createElement("p");
    count.className = "pr-gal__count"; count.setAttribute("aria-live", "polite");
    stage.appendChild(prev); stage.appendChild(next); stage.appendChild(count);

    function current() {
      var ts = thumbs();
      for (var i = 0; i < ts.length; i++) if (isActive(ts[i])) return i;
      return 0;
    }

    /* write only what changed: the watcher below must never be woken by this function's own writes */
    function set(el, prop, val) { if (el[prop] !== val) el[prop] = val; }
    function render() {
      var ts = thumbs(), n = ts.length, i = current();
      var many = n > 1;
      set(prev, "hidden", !many); set(next, "hidden", !many); set(count, "hidden", !many);
      if (!many) return;
      set(prev, "disabled", i <= 0);
      set(next, "disabled", i >= n - 1);
      set(count, "textContent", (i + 1) + " of " + n);
    }
    var own = [prev, next, count];

    function go(i) {
      var ts = thumbs();
      if (i < 0 || i >= ts.length) return;
      ts[i].click();
      /* keep the chosen thumbnail in view inside its rail, without moving the page */
      var rail = ts[i].parentElement;
      if (rail && rail.scrollWidth > rail.clientWidth) {
        var left = ts[i].offsetLeft - rail.offsetLeft - (rail.clientWidth - ts[i].offsetWidth) / 2;
        rail.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
      }
      render();
    }

    prev.addEventListener("click", function () { go(current() - 1); });
    next.addEventListener("click", function () { go(current() + 1); });

    g.addEventListener("keydown", function (e) {
      if (e.target.closest && e.target.closest(cfg.thumb)) return; /* the rail has its own keys */
      if (e.key === "ArrowLeft") { e.preventDefault(); go(current() - 1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(current() + 1); }
    });

    /* swipe: a clear horizontal gesture on the photo moves one frame; vertical scroll is untouched */
    var sx = null, sy = null;
    stage.addEventListener("pointerdown", function (e) {
      if (e.pointerType === "mouse") return;
      sx = e.clientX; sy = e.clientY;
    }, { passive: true });
    stage.addEventListener("pointerup", function (e) {
      if (sx === null) return;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      sx = sy = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) go(current() + (dx < 0 ? 1 : -1));
    }, { passive: true });
    stage.addEventListener("pointercancel", function () { sx = sy = null; }, { passive: true });

    /* any other way the photo changes (a thumbnail tap, a finish or variant switch) re-renders */
    g.addEventListener("click", function () { window.requestAnimationFrame(render); });
    if ("MutationObserver" in window) {
      new MutationObserver(function (muts) {
        for (var k = 0; k < muts.length; k++) {
          if (own.indexOf(muts[k].target) === -1) { render(); return; }
        }
      }).observe(g, {
        subtree: true, attributes: true, attributeFilter: ["class", "hidden", "aria-selected", "aria-current"]
      });
    }
    render();
  }

  var PATTERNS = [
    { root: "[data-gallery]", thumb: "[data-g-go]", stage: ".pp-g__stage, .lb-g__stage", frames: true },
    { root: ".gallery", thumb: ".thumb", stage: ".stage", frames: false },
    { root: ".pdp-gallery", thumb: ".pdp-thumb", stage: ".pdp-gallery__main", frames: false }
  ];

  function init() {
    PATTERNS.forEach(function (p) {
      arr(document.querySelectorAll(p.root)).forEach(function (g) { wire(g, p); });
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
